import React from 'react';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  NativeSelect,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import { cancelDonation, logger } from '@kuow/core';

const CONFIRM_MESSAGE =
  'Are you sure you would like to cancel your monthly donation to KUOW and its programming?';

const CANCEL_REASONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Select a reason' },
  { value: 'financial_reasons_cited', label: 'Financial reasons' },
  { value: 'giving_one_time_payments', label: 'Plan to give one time contributions going forward' },
  { value: 'giving_through_ira_family_foundation', label: 'Giving through a different channel or private fund' },
  { value: 'moved_away', label: 'Moved/Moving away' },
  { value: 'unhappy_with_kuow', label: 'Unhappy with KUOW programming' },
  { value: 'other', label: 'Other' },
];

interface CancelDonationDialogProps {
  open: boolean;
  donationId: number | null;
  onOpenChange: (open: boolean) => void;
  onCancelled: () => void | Promise<void>;
}

export const CancelDonationDialog: React.FC<CancelDonationDialogProps> = ({
  open,
  donationId,
  onOpenChange,
  onCancelled,
}) => {
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const reset = React.useCallback(() => {
    setReason('');
    setSubmitting(false);
    setError(null);
  }, []);

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) reset();
    onOpenChange(details.open);
  };

  const handleConfirm = (event: React.FormEvent) => {
    event.preventDefault();
    if (donationId === null) return;

    void (async () => {
      setSubmitting(true);
      setError(null);
      try {
        const cancelReasonCode = reason === '' ? 'other' : reason;
        await cancelDonation(donationId, { cancel_reason_code: cancelReasonCode }, { timeout: 60_000 });
      } catch (err) {
        logger.error('Failed to cancel monthly donation', { donationId, error: err });
        setError(
          err instanceof Error && err.message
            ? err.message
            : "Something went wrong on our end, and your monthly donation wasn't canceled. Please try again, or call us at (206) 221-2501.",
        );
        setSubmitting(false);
        return;
      }

      try {
        await onCancelled();
      } catch (refreshError) {
        logger.error('Donation cancelled, but refresh failed', { donationId, error: refreshError });
      }
      onOpenChange(false);
      reset();
    })();
  };

  return (
    <Dialog.Root lazyMount open={open} onOpenChange={handleOpenChange}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner px={{ base: '4', md: '6' }} py={{ base: '4', md: '8' }}>
          <Dialog.Content w="full" maxW="md" mx="4" borderRadius="l3" overflow="hidden">
            <Dialog.Header px="6" pt="6" pb="4">
              <Dialog.Title>Confirm</Dialog.Title>
            </Dialog.Header>

            <Box asChild>
              <form onSubmit={handleConfirm} noValidate>
                <Dialog.Body px="6" pb="4">
                  <Stack gap={4}>
                    <Text>{CONFIRM_MESSAGE}</Text>

                    <Field.Root>
                      <Field.Label>Reason for cancellation (optional)</Field.Label>
                      <NativeSelect.Root disabled={submitting}>
                        <NativeSelect.Field
                          value={reason}
                          onChange={(event) => setReason(event.currentTarget.value)}
                        >
                          {CANCEL_REASONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>

                    {error ? (
                      <Box
                        bg="red.subtle"
                        border="1px solid"
                        borderColor="red.emphasized"
                        color="red.fg"
                        px={4}
                        py={3}
                        borderRadius="l2"
                        fontSize="sm"
                      >
                        {error}
                      </Box>
                    ) : null}
                  </Stack>
                </Dialog.Body>

                <Dialog.Footer px="6" pb="6">
                  <HStack gap={3} justify="flex-end" w="full">
                    <Button
                      type="button"
                      variant="outline"
                      colorPalette="gray"
                      disabled={submitting}
                      onClick={() => {
                        reset();
                        onOpenChange(false);
                      }}
                    >
                      No, keep my donation
                    </Button>
                    <Button
                      type="submit"
                      colorPalette="river"
                      loading={submitting}
                      disabled={donationId === null}
                    >
                      Yes, cancel my donation
                    </Button>
                  </HStack>
                </Dialog.Footer>
              </form>
            </Box>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" disabled={submitting} />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
